/**
 * ResultsPage Component
 * 
 * Main page for displaying test results, profile analysis, and recommendations.
 * - Profile: Instant card + PDF report
 * - Recommendations: Separate tab with LLM-generated advice + PDF report
 */
import React from 'react';
import { useParams } from 'react-router-dom';

// Hooks
import { useResults, usePdfViewer } from '../hooks';

// Components
import {
  ResultsHeader,
  SummaryTab,
  ProfileTab,
  RecommendationsTab,
  DimensionsTab,
  AnswersTab,
  TimesTab,
  PdfViewerModal
} from '../components/results';

// Utils
import { generateRecommendationsPdfUrl, downloadRecommendationsPdf } from '../utils/pdfGenerator';

const ResultsPage: React.FC = () => {
  const { attemptId } = useParams();
  
  // Custom hooks for state management
  const {
    results,
    loading,
    loadingProfile,
    loadingRecommendations,
    profileReport,
    recommendations,
    activeTab,
    setActiveTab,
    loadProfileReport,
    loadRecommendations
  } = useResults(attemptId);

  const {
    showPdfModal,
    pdfUrl,
    generatingPdf,
    openPdfViewer,
    closePdfModal,
    downloadPdf
  } = usePdfViewer(results);

  // State for recommendations PDF
  const [showRecsPdfModal, setShowRecsPdfModal] = React.useState(false);
  const [recsPdfUrl, setRecsPdfUrl] = React.useState<string | null>(null);
  const [generatingRecsPdf, setGeneratingRecsPdf] = React.useState(false);

  const openRecsPdfViewer = () => {
    if (!recommendations || !profileReport) return;
    
    setGeneratingRecsPdf(true);
    try {
      const url = generateRecommendationsPdfUrl({
        profileName: profileReport.profile_name,
        recommendations: recommendations.recommendations
      });
      if (url) {
        setRecsPdfUrl(url);
        setShowRecsPdfModal(true);
      }
    } catch (err) {
      console.error('Error generating recommendations PDF:', err);
    } finally {
      setGeneratingRecsPdf(false);
    }
  };

  const handleDownloadRecsPdf = () => {
    if (!recommendations || !profileReport) return;
    
    setGeneratingRecsPdf(true);
    try {
      downloadRecommendationsPdf({
        profileName: profileReport.profile_name,
        recommendations: recommendations.recommendations
      });
    } catch (err) {
      console.error('Error downloading recommendations PDF:', err);
    } finally {
      setGeneratingRecsPdf(false);
    }
  };

  // Loading state
  if (loading) {
    return <LoadingScreen message="Chargement des resultats..." />;
  }

  if (!results) {
    return <LoadingScreen message="Aucun resultat trouve" />;
  }

  return (
    <div className="min-h-screen bg-neutral p-4">
      <div className="max-w-xl mx-auto">
        {/* Header with scores and tabs */}
        <ResultsHeader
          results={results}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab content */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          {activeTab === 'summary' && (
            <SummaryTab results={results} />
          )}

          {activeTab === 'profile' && (
            <ProfileTab
              results={results}
              loadingProfile={loadingProfile}
              profileReport={profileReport}
              generatingPdf={generatingPdf}
              onOpenPdfViewer={openPdfViewer}
              onDownloadPdf={downloadPdf}
              onLoadProfile={loadProfileReport}
            />
          )}

          {activeTab === 'recommendations' && (
            <RecommendationsTab
              loadingRecommendations={loadingRecommendations}
              recommendations={recommendations}
              profileName={profileReport?.profile_name || 'Votre profil'}
              onLoadRecommendations={loadRecommendations}
              onViewRecommendationsPdf={openRecsPdfViewer}
              onDownloadRecommendationsPdf={handleDownloadRecsPdf}
              generatingPdf={generatingRecsPdf}
            />
          )}

          {activeTab === 'dimensions' && (
            <DimensionsTab results={results} />
          )}

          {activeTab === 'answers' && (
            <AnswersTab results={results} />
          )}

          {activeTab === 'times' && (
            <TimesTab results={results} />
          )}
        </div>
      </div>

      {/* Profile PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={showPdfModal}
        pdfUrl={pdfUrl}
        version={results.version}
        onClose={closePdfModal}
        onDownload={downloadPdf}
      />

      {/* Recommendations PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={showRecsPdfModal}
        pdfUrl={recsPdfUrl}
        version={`Recommandations - ${profileReport?.profile_name || ''}`}
        onClose={() => setShowRecsPdfModal(false)}
        onDownload={handleDownloadRecsPdf}
      />
    </div>
  );
};

interface LoadingScreenProps {
  message: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-neutral">
    <p className="text-gray-600">{message}</p>
  </div>
);

export default ResultsPage;
